import React, { useEffect } from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useOutletContext,
} from 'react-router-dom'
import { Box, Grid, Typography } from '@mui/material'
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import { CommonButton } from '@/components/common'
import {
  DriveResultsHeader,
  DrivePerformanceCard,
  BloodGroupTable,
  DonorsWhoGaveTable,
} from '@/components/chairperson/drive-results'
import { ROUTES } from '@/constants'
import {
  useGetDriveResults,
  useGetDriveDonors,
  useExportDriveDonorsExcel,
  useExportDriveSummaryPdf,
} from '@/hooks'
import { getChairpersonDriveBackTarget } from '@/utils/chairpersonNavigation'

export const ChairpersonDriveResultsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { driveId } = useParams()
  const { selectedAccountId } = useOutletContext()
  const fromTab = location.state?.fromTab ?? 'past'
  const returnTo = location.state?.returnTo
  const backTarget = getChairpersonDriveBackTarget({
    returnTo,
    fromTab,
    driveId,
  })

  const {
    driveResults,
    loadingDriveResults,
    driveResultsError,
    getDriveResults,
  } = useGetDriveResults()
  const {
    driveDonors,
    driveDonorsTotal,
    loadingDriveDonors,
    showingAllDriveDonors,
    getDriveDonors,
    viewAllDriveDonors,
  } = useGetDriveDonors()
  const {
    exportDonorsExcel,
    loadingExportDonorsExcel,
    exportDonorsExcelError,
  } = useExportDriveDonorsExcel()
  const { exportSummaryPdf, loadingExportSummaryPdf, exportSummaryPdfError } =
    useExportDriveSummaryPdf()

  useEffect(() => {
    if (!driveId || !selectedAccountId) return
    getDriveResults(driveId, selectedAccountId)
    getDriveDonors(driveId, selectedAccountId)
  }, [driveId, selectedAccountId])

  const handleBack = () => {
    navigate(
      backTarget.to,
      backTarget.state ? { state: backTarget.state } : undefined
    )
  }

  const handleExportDonors = () => {
    exportDonorsExcel(
      driveId,
      selectedAccountId,
      `${driveResults?.drive?.name || 'drive'}-donors.xlsx`
    )
  }

  const handleExportSummary = () => {
    exportSummaryPdf(
      driveId,
      selectedAccountId,
      `${driveResults?.drive?.name || 'drive'}-summary.pdf`
    )
  }

  if (!selectedAccountId) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Select an account to view drive results.
      </Typography>
    )
  }

  if (driveResultsError) {
    return (
      <Box>
        <CommonButton
          variant='text'
          startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={handleBack}
        >
          {backTarget.label}
        </CommonButton>
        <Typography variant='body1' color='error' sx={{ mt: 2 }}>
          {driveResultsError?.response?.status === 403
            ? "You don't have access to this drive's results."
            : 'Unable to load drive results.'}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <CommonButton
        variant='text'
        startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        {backTarget.label}
      </CommonButton>

      <DriveResultsHeader
        drive={driveResults?.drive}
        loading={loadingDriveResults}
        onDownloadSummary={handleExportSummary}
        loadingDownloadSummary={loadingExportSummaryPdf}
      />
      {exportSummaryPdfError && (
        <Typography variant='body2' color='error' sx={{ mb: 2 }}>
          Unable to download the drive summary. Please try again.
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DrivePerformanceCard
            performance={driveResults?.performance}
            loading={loadingDriveResults}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <BloodGroupTable
            bloodGroups={driveResults?.blood_groups}
            loading={loadingDriveResults}
          />
        </Grid>
      </Grid>

      <DonorsWhoGaveTable
        donors={driveDonors}
        total={driveDonorsTotal}
        loading={loadingDriveDonors}
        showingAll={showingAllDriveDonors}
        onViewAll={() => viewAllDriveDonors(driveId, selectedAccountId)}
        onExport={handleExportDonors}
        loadingExport={loadingExportDonorsExcel}
      />
      {exportDonorsExcelError && (
        <Typography variant='body2' color='error' sx={{ mt: 1 }}>
          Unable to download the donor list. Please try again.
        </Typography>
      )}
    </Box>
  )
}
